import * as THREE from 'three';

import { AudioControls, AudioProvider } from './utils/AudioManager';
import {
  CameraControlsUI,
  DebugToggle,
  OrbitDebugCamera,
} from './utils/DebugControls';
import { Canvas, Vector3, useFrame, useThree } from '@react-three/fiber';
import { DebugProvider, useDebugMode } from './utils/DebugContext';
import { NPCState, createInitialNPCs } from './utils/NPCSpawner';
import { Suspense, useCallback, useEffect, useRef, useState } from 'react';

import { CharacterSelectModal } from './components/CharacterSelectModal';
import { ContraMissionScene } from './components/missions/contra/ContraMissionScene';
import { DebugInfo } from './components/DebugInfo';
import { Environment } from './components/Environment';
import { Leaderboard } from './components/Leaderboard';
import { MobileControls } from './components/MobileControls';
import { PHYSICS } from './constants';
import { PlayerNameModal } from './components/PlayerNameModal';
import { Rasengan } from './components/Rasengan';
import { Shuriken } from './components/Shuriken';
import { SimpleChicken } from './components/SimpleChicken';
import { SimpleGenin } from './components/SimpleGenin';
import { SimpleNaruto } from './components/SimpleNaruto';
import { SimpleSakura } from './components/SimpleSakura';
import { SimpleSasuke } from './components/SimpleSasuke';
import { Stats } from '@react-three/drei';
import { useKeyboardControls } from './engine/useKeyboardControls';
import usePartySocket from 'partysocket/react';

// Define types for our debug information
interface DebugInfo {
  position: [number, number, number];
  facingDirection?: [number, number, number];
  isJumping?: boolean;
  keys: {
    forward: boolean;
    backward: boolean;
    left: boolean;
    right: boolean;
    jump: boolean;
    run: boolean;
    rasengan?: boolean;
    shuriken?: boolean;
  };
  rasengans?: number;
  shurikens?: number;
  throwRasengan?: {
    position: [number, number, number];
    direction: [number, number, number];
  };
  throwShuriken?: {
    position: [number, number, number];
    direction: [number, number, number];
  };
}

interface CameraConfig {
  height: number;
  distance: number;
  lateralOffset: number;
  minSmoothness: number;
  maxSmoothness: number;
  followThreshold: number; // Maximum distance before camera repositions
}
export type PlayerState = {
  id: string;
  position: [number, number, number];
  facingDirection?: [number, number, number];
  isJumping?: boolean;
  color?: string;
  lastUpdate: number;
  health: number; // Player's health from 0-100
  characterType?: string; // Add this field to track character selection
  score: number; // Player's score for leaderboard
  displayName?: string; // Player's chosen display name for leaderboard
};

export type Shuriken = {
  id: string;
  position: [number, number, number];
  playerId: string;
  createdAt: number;
};

export type Rasengan = {
  id: string;
  position: [number, number, number];
  playerId: string;
  createdAt: number;
};

export type LeaderboardEntry = {
  playerId: string;
  playerName: string;
  score: number;
  lastUpdated: number; // timestamp
};

export type LeaderboardData = {
  entries: LeaderboardEntry[];
  lastUpdated: number;
};

export type GameState = {
  players: Record<string, PlayerState>;
  shurikens: Shuriken[];
  rasengans?: Rasengan[];
  npcs: NPCState[]; // Add NPCs to the game state
  leaderboard?: LeaderboardData; // Add leaderboard to the game state
};

export type GameAction =
  | { type: 'init'; state: GameState }
  | { type: 'gameState'; state: GameState; timestamp: number }
  | { type: 'playerJoined'; player: PlayerState }
  | { type: 'playerLeft'; playerId: string }
  | { type: 'positionCorrection'; position: [number, number, number] }
  | { type: 'newShuriken'; shuriken: Shuriken }
  | { type: 'newRasengan'; rasengan: Rasengan }
  | {
      type: 'playerHit';
      playerId: string;
      health: number;
      position: [number, number, number];
      projectileType: string;
      sourcePlayerId: string;
    }
  | {
      type: 'playerRespawn';
      playerId: string;
      position: [number, number, number];
      characterType?: string;
    }
  | {
      type: 'selectCharacter';
      playerId: string;
      characterType: string;
    }
  | {
      type: 'move';
      position?: Vector3;
      facingDirection?: Vector3;
      isJumping?: boolean;
    }
  | { type: 'jump' }
  | { type: 'throwShuriken'; direction: Vector3 }
  | { type: 'throwRasengan'; direction: Vector3 }
  | { type: 'npcUpdate'; npcs: NPCState[] }
  | {
      type: 'npcHit';
      npcId: string;
      damage: number;
      sourcePlayerId: string;
      position: [number, number, number];
    }
  | { type: 'npcRespawn'; npc: NPCState }
  | {
      type: 'scoreUpdate';
      playerId: string;
      points: number;
      newTotal: number;
    }
  | {
      type: 'submitScore';
      playerId: string;
      playerName: string;
      score: number;
    }
  | {
      type: 'leaderboardUpdate';
      leaderboard: LeaderboardData;
    };

// Define mission types
export type MissionType = 'none' | 'contra' | 'mariokart' | 'megaman';

function AppContent() {
  const [gameState, setGameState] = useState<GameState>({
    players: {},
    shurikens: [],
    rasengans: [],
    npcs: [], // Initialize with empty NPCs array
    leaderboard: { entries: [], lastUpdated: Date.now() }, // Initialize empty leaderboard
  });
  const [playerId, setPlayerId] = useState<string | null>(null);
  const [localPlayerPosition, setLocalPlayerPosition] = useState<
    [number, number, number]
  >([0, 0, 0]);
  const lastServerUpdate = useRef<number>(Date.now());
  const GROUND_LEVEL = PHYSICS.GROUND_LEVEL; // Use shared constant

  // Add character selection state
  const [selectedCharacter, setSelectedCharacter] = useState<string | null>(
    null
  );

  // Add mission state
  const [activeMission, setActiveMission] = useState<MissionType>('none');

  // Add state for showing player name modal
  const [showNameModal, setShowNameModal] = useState(false);
  const [scoreToSubmit, setScoreToSubmit] = useState(0);

  // Add constants for score thresholds
  const SCORE_SUBMISSION_THRESHOLD = 50;
  const POINTS_FOR_PLAYER_HIT = 1;
  const POINTS_FOR_SNAKE_HIT = 1;
  const POINTS_FOR_OROCHIMARU_HIT = 2;

  // Add state for score animation
  const [scoreAnimation, setScoreAnimation] = useState<{
    points: number;
    visible: boolean;
    position: { x: number; y: number };
  } | null>(null);

  // Mission management functions
  const enterMission = (missionType: MissionType) => {
    console.log(`Entering mission: ${missionType}`);
    setActiveMission(missionType);
  };

  const exitMission = () => {
    console.log('Exiting mission, returning to main world');
    setActiveMission('none');
  };

  const [debugInfo, setDebugInfo] = useState<DebugInfo>({
    position: [0, 0, 0],
    keys: {
      forward: false,
      backward: false,
      left: false,
      right: false,
      jump: false,
      run: false,
    },
  });

  const { isDebugMode } = useDebugMode();

  // Current player score display
  const ScoreDisplay = () => {
    if (!playerId || !gameState.players[playerId]) return null;

    const currentScore = gameState.players[playerId].score || 0;

    const scoreStyle: React.CSSProperties = {
      position: 'fixed',
      bottom: '20px',
      right: '20px',
      backgroundColor: 'rgba(0, 0, 0, 0.7)',
      color: 'white',
      padding: '10px 15px',
      borderRadius: '5px',
      fontSize: '18px',
      fontWeight: 'bold',
      zIndex: 1000,
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
    };

    return (
      <div style={scoreStyle}>
        <span>Score:</span>
        <span style={{ color: '#FFD700' }}>{currentScore}</span>
      </div>
    );
  };

  // Initial camera configuration for drone-like view
  const [cameraConfig, setCameraConfig] = useState<CameraConfig>({
    height: 20, // Higher up for better overview
    distance: 50, // Slight offset behind character
    lateralOffset: 0, // Centered above character
    minSmoothness: 0.1,
    maxSmoothness: 1.0,
    followThreshold: 10, // Units the camera can lag behind before teleporting
  });

  const isMobile = /iPhone|iPad|iPod|Android/i.test(
    typeof navigator !== 'undefined' ? navigator.userAgent : ''
  );

  const updateDebugInfo = (data: Partial<DebugInfo>) => {
    setDebugInfo((prev) => ({ ...prev, ...data }));

    // Send position and movement data to server
    if (socket && playerId && data.position) {
      // Apply ground level constraint
      const [x, y, z] = data.position as [number, number, number];
      const fixedPosition: [number, number, number] = [
        x,
        Math.max(y, GROUND_LEVEL), // Ensure Y position doesn't go below ground level
        z,
      ];

      // Store local position for client-side prediction
      setLocalPlayerPosition(fixedPosition);

      // Send update to server
      socket.send(
        JSON.stringify({
          type: 'move',
          position: fixedPosition,
          facingDirection: data.facingDirection,
          isJumping: data.isJumping,
        })
      );
    }

    // Send rasengan data to server
    if (socket && playerId && data.throwRasengan) {
      socket.send(
        JSON.stringify({
          type: 'throwRasengan',
          position: data.throwRasengan.position,
          direction: data.throwRasengan.direction,
          playerId,
        })
      );
    }

    // Send shuriken data to server
    if (socket && playerId && data.throwShuriken) {
      socket.send(
        JSON.stringify({
          type: 'throwShuriken',
          position: data.throwShuriken.position,
          direction: data.throwShuriken.direction,
          playerId,
        })
      );
    }
  };

  // Handle mobile controls
  const handleMobileControlsChange = useCallback(
    (controls: any) => {
      // Update the keys state based on mobile controls
      const updatedKeys = {
        ...debugInfo.keys,
        ...controls,
      };

      // Dispatch a custom event that SimpleNaruto can listen for
      const event = new CustomEvent('controlsupdate', {
        detail: { keys: updatedKeys },
      });
      window.dispatchEvent(event);

      // Also update the debug info
      setDebugInfo((prev) => ({
        ...prev,
        keys: updatedKeys,
      }));
    },
    [debugInfo.keys]
  );

  // Use keyboard controls for desktop
  const keyboardControls = useKeyboardControls();

  // Update debugInfo with keyboard controls for HUD display
  useEffect(() => {
    if (!isMobile) {
      setDebugInfo((prev) => ({ ...prev, keys: keyboardControls }));
    }
  }, [keyboardControls]);

  // NOTE: We're no longer merging the controls in App.tsx
  // Instead, the SimpleNaruto component will handle both keyboard and mobile controls directly

  // Connect to the PartyKit server
  const socket = usePartySocket({
    room: 'konoha-village',
    onMessage(event) {
      try {
        const action = JSON.parse(event.data) as GameAction;
        lastServerUpdate.current = Date.now();

        switch (action.type) {
          case 'init':
            setGameState(action.state);
            setPlayerId(socket.id);

            // When we initialize, send our character selection if we already have one
            if (selectedCharacter) {
              socket.send(
                JSON.stringify({
                  type: 'selectCharacter',
                  characterType: selectedCharacter,
                  playerId: socket.id,
                })
              );
            }

            // If no NPCs in the initial state, generate some and notify server
            if (action.state.npcs.length === 0 && socket.id) {
              // Only the first connected player should generate NPCs
              // Generate 100 NPCs (adjust this value based on your performance needs)
              const generatedNPCs = createInitialNPCs(20);

              // Send NPCs to server
              socket.send(
                JSON.stringify({
                  type: 'npcUpdate',
                  npcs: generatedNPCs,
                })
              );
            }
            break;

          case 'gameState':
            // For the local player, we use client-side prediction
            // For other players, we use the server state
            if (playerId) {
              setGameState((prev) => {
                const newState = { ...action.state };

                // If we have local position data for our player, use it for smoother client-side prediction
                if (prev.players[playerId] && localPlayerPosition) {
                  newState.players[playerId] = {
                    ...newState.players[playerId],
                    position: localPlayerPosition,
                    // Make sure to preserve our character selection
                    characterType:
                      prev.players[playerId].characterType ||
                      selectedCharacter ||
                      'naruto',
                  };
                }

                // Preserve character types for all players when getting server updates
                Object.keys(prev.players).forEach((id) => {
                  if (newState.players[id] && prev.players[id].characterType) {
                    newState.players[id] = {
                      ...newState.players[id],
                      characterType: prev.players[id].characterType,
                    };
                  }
                });

                // Ensure all players have valid Y positions
                Object.keys(newState.players).forEach((id) => {
                  if (id !== playerId) {
                    // Skip local player as we already handled it
                    const player = newState.players[id];
                    if (player.position[1] < GROUND_LEVEL) {
                      player.position[1] = GROUND_LEVEL;
                    }
                  }
                });

                // Make sure NPCs are included in the state
                if (!newState.npcs && prev.npcs) {
                  newState.npcs = prev.npcs;
                }

                return newState;
              });
            } else {
              setGameState(action.state);
            }
            break;

          case 'positionCorrection':
            // Server is correcting our position due to invalid movement
            if (playerId) {
              // Ensure the corrected position respects ground level
              const [x, y, z] = action.position;
              setLocalPlayerPosition([x, Math.max(y, GROUND_LEVEL), z]);
            }
            break;

          case 'playerJoined':
            // When a player joins, we need to make sure their character is set
            setGameState((prev) => {
              // If this is our player joining and we have a character selected,
              // make sure it's assigned
              const newPlayer = {
                ...action.player,
                score: action.player.score || 0, // Ensure player has a score
              };

              // If this is the local player and we have a character selected
              if (socket.id === action.player.id && selectedCharacter) {
                newPlayer.characterType = selectedCharacter;
              }

              return {
                ...prev,
                players: {
                  ...prev.players,
                  [action.player.id]: newPlayer,
                },
              };
            });
            break;

          case 'playerLeft':
            setGameState((prev) => {
              const newPlayers = { ...prev.players };
              delete newPlayers[action.playerId];
              return {
                ...prev,
                players: newPlayers,
              };
            });
            break;

          // Handle character selection updates from other players
          case 'selectCharacter':
            // Don't check for existing player - just update the character type for any player ID
            setGameState((prev) => {
              const player = prev.players[action.playerId];
              if (!player) {
                // If the player doesn't exist yet, we can't update them
                console.log(
                  `Player ${action.playerId} not found in state, can't update character`
                );
                return prev;
              }

              return {
                ...prev,
                players: {
                  ...prev.players,
                  [action.playerId]: {
                    ...player,
                    characterType: action.characterType,
                  },
                },
              };
            });
            break;

          case 'newShuriken':
            setGameState((prev) => ({
              ...prev,
              shurikens: [...prev.shurikens, action.shuriken],
            }));
            break;

          case 'newRasengan':
            setGameState((prev) => ({
              ...prev,
              rasengans: [...(prev.rasengans || []), action.rasengan],
            }));
            break;

          case 'playerHit':
            setGameState((prev) => ({
              ...prev,
              players: {
                ...prev.players,
                [action.playerId]: {
                  ...prev.players[action.playerId],
                  health: action.health,
                },
              },
            }));

            // Award points to the source player for hitting another player
            if (action.sourcePlayerId === playerId) {
              incrementPlayerScore(POINTS_FOR_PLAYER_HIT);
            }

            // Check if this is the local player and they died
            if (action.playerId === playerId && action.health <= 0) {
              // Check if player has enough score for the modal
              const playerScore = gameState.players[playerId]?.score || 0;
              if (
                playerScore >= SCORE_SUBMISSION_THRESHOLD &&
                (!gameState.players[playerId]?.displayName ||
                  gameState.players[playerId]?.displayName === '')
              ) {
                setScoreToSubmit(playerScore);
                setShowNameModal(true);
              }
            }
            break;

          case 'playerRespawn':
            setGameState((prev) => ({
              ...prev,
              players: {
                ...prev.players,
                [action.playerId]: {
                  ...prev.players[action.playerId],
                  position: action.position,
                  health: 100,
                  score: 0, // Reset score to 0 when player respawns
                  characterType:
                    action.characterType ||
                    prev.players[action.playerId].characterType,
                },
              },
            }));

            // If this is the local player, update the local position too
            if (action.playerId === playerId) {
              setLocalPlayerPosition(action.position);
            }
            break;

          // Add NPC-specific action handlers
          case 'npcUpdate':
            setGameState((prev) => ({
              ...prev,
              npcs: action.npcs,
            }));
            break;

          case 'npcHit':
            setGameState((prev) => {
              // Find the NPC and update its health
              const updatedNPCs = prev.npcs.map((npc) => {
                if (npc.id === action.npcId) {
                  // Calculate direction away from damage source
                  const dx = npc.position[0] - action.position[0];
                  const dz = npc.position[2] - action.position[2];

                  // Normalize
                  const length = Math.sqrt(dx * dx + dz * dz);
                  const fleeDirection: [number, number, number] = [
                    dx / length,
                    0,
                    dz / length,
                  ];

                  const newHealth = Math.max(0, npc.health - action.damage);

                  // If dead
                  if (newHealth <= 0) {
                    return {
                      ...npc,
                      health: 0,
                      behaviorState: 'dead' as const,
                      respawnTime: Date.now() + 10000, // Respawn after 10 seconds
                    };
                  }

                  // If health is low, start fleeing
                  if (newHealth < 30) {
                    return {
                      ...npc,
                      health: newHealth,
                      behaviorState: 'fleeing' as const,
                      facingDirection: fleeDirection,
                    };
                  }

                  // Just take damage
                  return {
                    ...npc,
                    health: newHealth,
                  };
                }
                return npc;
              });

              return {
                ...prev,
                npcs: updatedNPCs,
              };
            });

            // Award points to the player for hitting an NPC
            if (action.sourcePlayerId === playerId) {
              // Check NPC type to determine points
              const hitNpc = gameState.npcs.find(
                (npc) => npc.id === action.npcId
              );
              if (hitNpc) {
                // Award points based on NPC type
                const pointsToAward =
                  hitNpc.npcType === 'chicken'
                    ? POINTS_FOR_SNAKE_HIT
                    : POINTS_FOR_OROCHIMARU_HIT;

                incrementPlayerScore(pointsToAward);
              }
            }
            break;

          case 'npcRespawn':
            setGameState((prev) => {
              const updatedNPCs = prev.npcs.map((npc) =>
                npc.id === action.npc.id ? action.npc : npc
              );
              return {
                ...prev,
                npcs: updatedNPCs,
              };
            });
            break;

          case 'scoreUpdate':
            setGameState((prev) => ({
              ...prev,
              players: {
                ...prev.players,
                [action.playerId]: {
                  ...prev.players[action.playerId],
                  score: action.newTotal,
                },
              },
            }));
            break;

          case 'submitScore':
            // Implement submitScore logic
            break;

          case 'leaderboardUpdate':
            setGameState((prev) => ({
              ...prev,
              leaderboard: action.leaderboard,
            }));
            break;
        }
      } catch (error) {
        console.error('Error parsing message:', error);
      }
    },
  });

  // Handle character selection and notify server
  const handleSelectCharacter = (character: string) => {
    setSelectedCharacter(character);

    // Send character selection to server if connected
    if (socket && playerId) {
      socket.send(
        JSON.stringify({
          type: 'selectCharacter',
          characterType: character,
          playerId: playerId, // Include player ID to be sure server knows who's selecting
        })
      );
    }
  };

  // Add function to increment player score
  const incrementPlayerScore = useCallback(
    (points: number) => {
      if (!playerId) return;

      const currentPlayer = gameState.players[playerId];
      if (!currentPlayer) return;

      // Handle score reset from Contra mission (large negative value)
      const isResetCommand = points <= -999999;

      // Calculate new score (never go below 0)
      const newScore = isResetCommand
        ? 0
        : Math.max(0, (currentPlayer.score || 0) + points);

      // Send score update to server
      if (socket) {
        socket.send(
          JSON.stringify({
            type: 'scoreUpdate',
            playerId,
            points: isResetCommand ? -currentPlayer.score : points, // Adjust points value for reset
            newTotal: newScore,
          })
        );
      }

      // Update local state immediately for responsiveness
      setGameState((prev) => ({
        ...prev,
        players: {
          ...prev.players,
          [playerId]: {
            ...prev.players[playerId],
            score: newScore,
          },
        },
      }));

      // Only show score animation for normal score changes (not resets)
      if (!isResetCommand) {
        // Show score animation
        const randomOffset = {
          x: Math.random() * 100 - 50, // -50 to 50px
          y: -Math.random() * 50 - 20, // -20 to -70px (upward)
        };

        setScoreAnimation({
          points,
          visible: true,
          position: randomOffset,
        });

        // Hide animation after 1.5 seconds
        setTimeout(() => {
          setScoreAnimation(null);
        }, 1500);
      }

      // Check if score crossed threshold for name submission
      if (
        newScore >= SCORE_SUBMISSION_THRESHOLD &&
        (!currentPlayer.displayName || currentPlayer.displayName === '')
      ) {
        setScoreToSubmit(newScore);
        setShowNameModal(true);
      }
    },
    [playerId, gameState.players, socket]
  );

  // Handle name submission for leaderboard
  const handleNameSubmit = (name: string) => {
    if (!playerId || !socket) return;

    // Send score with name to server
    socket.send(
      JSON.stringify({
        type: 'submitScore',
        playerId,
        playerName: name,
        score: scoreToSubmit,
      })
    );

    // Update local player state
    setGameState((prev) => ({
      ...prev,
      players: {
        ...prev.players,
        [playerId]: {
          ...prev.players[playerId],
          displayName: name,
        },
      },
    }));

    // Close modal
    setShowNameModal(false);
  };

  // Handle canceling name submission
  const handleNameCancel = () => {
    setShowNameModal(false);
  };

  // Score Animation Component
  const ScoreAnimationEffect = () => {
    if (!scoreAnimation || !scoreAnimation.visible) return null;

    const { points, position } = scoreAnimation;

    const animationStyle: React.CSSProperties = {
      position: 'fixed',
      top: '50%',
      left: '50%',
      transform: `translate(${position.x}px, ${position.y}px)`,
      color: points > 1 ? '#FFD700' : '#FFFFFF',
      fontSize: points > 1 ? '32px' : '24px',
      fontWeight: 'bold',
      textShadow: '0 0 10px rgba(0, 0, 0, 0.7)',
      pointerEvents: 'none',
      animation: 'float-up 1.5s ease-out forwards',
      zIndex: 2000,
    };

    // Add animation keyframes
    const styleElement = document.createElement('style');
    styleElement.innerHTML = `
      @keyframes float-up {
        0% {
          opacity: 0;
          transform: translate(${position.x}px, ${
      position.y + 20
    }px) scale(0.5);
        }
        20% {
          opacity: 1;
          transform: translate(${position.x}px, ${position.y}px) scale(1.2);
        }
        80% {
          opacity: 1;
          transform: translate(${position.x}px, ${position.y - 40}px) scale(1);
        }
        100% {
          opacity: 0;
          transform: translate(${position.x}px, ${
      position.y - 80
    }px) scale(0.8);
        }
      }
    `;
    document.head.appendChild(styleElement);

    // Clean up style element
    useEffect(() => {
      return () => {
        if (document.head.contains(styleElement)) {
          document.head.removeChild(styleElement);
        }
      };
    }, []);

    return (
      <div style={animationStyle}>
        +{points} {points === 1 ? 'point' : 'points'}!
      </div>
    );
  };

  // If character is not selected, show character selection screen
  if (!selectedCharacter) {
    return (
      <>
        {/* Render the game scene in the background */}
        <Canvas shadows>
          <ambientLight intensity={0.1} />
          <directionalLight
            castShadow
            position={[3, 2, 3]}
            intensity={1.0}
            shadow-mapSize-width={2048}
            shadow-mapSize-height={2048}
            shadow-camera-far={2}
            shadow-camera-left={-10}
            shadow-camera-right={10}
            shadow-camera-top={10}
            shadow-camera-bottom={-10}
          />
          <Environment playerPosition={localPlayerPosition} />
          {/* Add a dummy player to see in the background */}
          <SimpleNaruto
            position={[-3, 0, -3]}
            isControlled={false}
            health={100}
            isDead={false}
            facingDirection={[0, 0, 1]}
          />
          <SimpleSasuke
            position={[0, 0, -3]}
            isControlled={false}
            health={100}
            isDead={false}
            facingDirection={[0, 0, 1]}
          />
          <SimpleSakura
            position={[3, 0, -3]}
            isControlled={false}
            health={100}
            isDead={false}
            facingDirection={[0, 0, 1]}
          />
          <mesh
            rotation={[-Math.PI / 2, 0, 0]}
            position={[0, -0.5, 0]}
            receiveShadow
          >
            <planeGeometry args={[100, 100]} />
            <meshStandardMaterial color="#444444" />
          </mesh>
          <OrbitDebugCamera />
        </Canvas>

        {/* Overlay the character selection modal */}
        <CharacterSelectModal onSelect={handleSelectCharacter} />
      </>
    );
  }

  return (
    <>
      {/* Move DebugInfo outside the Canvas */}
      <DebugInfo debugInfo={debugInfo} />

      {/* Show Leaderboard if available */}
      {gameState.leaderboard && (
        <Leaderboard
          leaderboard={gameState.leaderboard}
          currentPlayerId={playerId || undefined}
        />
      )}

      {/* Show Name Modal if triggered */}
      {showNameModal && (
        <>
          {/* Pause game controls while modal is open */}
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100vw',
              height: '100vh',
              zIndex: 1999, // Just below the modal zIndex
              background: 'rgba(0, 0, 0, 0.3)', // Semi-transparent overlay
            }}
          />
          <PlayerNameModal
            onSubmit={handleNameSubmit}
            onCancel={handleNameCancel}
            score={scoreToSubmit}
          />
        </>
      )}

      <Canvas shadows>
        {/* Render mission world or main world based on active mission */}
        {activeMission === 'none' ? (
          <>
            {/* Main world */}
            <ambientLight intensity={0.1} />
            <directionalLight
              castShadow
              position={[3, 2, 3]}
              intensity={1.0}
              shadow-mapSize-width={2048}
              shadow-mapSize-height={2048}
              shadow-camera-far={2}
              shadow-camera-left={-10}
              shadow-camera-right={10}
              shadow-camera-top={10}
              shadow-camera-bottom={-10}
            />
            <Environment
              playerPosition={localPlayerPosition}
              onEnterMission={enterMission}
            />
            <Suspense fallback={null}>
              {/* Render players based on their characterType from server data */}
              {Object.entries(gameState.players).map(([_id, player]) => {
                const isCurrentPlayer = playerId === player.id;

                // For the current player, use local selection
                // For other players, use their characterType from server data (defaulting to naruto)
                const characterType = isCurrentPlayer
                  ? selectedCharacter
                  : player.characterType || 'naruto';

                if (characterType === 'sasuke') {
                  return (
                    <SimpleSasuke
                      key={player.id}
                      playerId={player.id}
                      position={player.position}
                      facingDirection={player.facingDirection}
                      isJumping={player.isJumping}
                      isControlled={isCurrentPlayer}
                      isCurrentPlayer={isCurrentPlayer}
                      updateDebugInfo={
                        isCurrentPlayer ? updateDebugInfo : undefined
                      }
                      networkShurikens={gameState.shurikens}
                      networkRasengans={gameState.rasengans}
                      health={player.health}
                    />
                  );
                } else if (characterType === 'sakura') {
                  return (
                    <SimpleSakura
                      key={player.id}
                      playerId={player.id}
                      position={player.position}
                      facingDirection={player.facingDirection}
                      isJumping={player.isJumping}
                      isControlled={isCurrentPlayer}
                      isCurrentPlayer={isCurrentPlayer}
                      updateDebugInfo={
                        isCurrentPlayer ? updateDebugInfo : undefined
                      }
                      networkShurikens={gameState.shurikens}
                      networkRasengans={gameState.rasengans}
                      health={player.health}
                    />
                  );
                } else {
                  return (
                    <SimpleNaruto
                      key={player.id}
                      playerId={player.id}
                      position={player.position}
                      facingDirection={player.facingDirection}
                      isJumping={player.isJumping}
                      isControlled={isCurrentPlayer}
                      isCurrentPlayer={isCurrentPlayer}
                      updateDebugInfo={
                        isCurrentPlayer ? updateDebugInfo : undefined
                      }
                      networkShurikens={gameState.shurikens}
                      networkRasengans={gameState.rasengans}
                      health={player.health}
                    />
                  );
                }
              })}

              {/* Render NPCs based on their npcType */}
              {gameState.npcs.map((npc) => {
                if (npc.npcType === 'chicken') {
                  return (
                    <SimpleChicken
                      key={npc.id}
                      npcId={npc.id}
                      position={[
                        npc.position[0],
                        npc.position[1] - 0.8,
                        npc.position[2],
                      ]}
                      facingDirection={npc.facingDirection}
                      health={npc.health}
                      behaviorState={npc.behaviorState}
                    />
                  );
                } else {
                  // Default to genin
                  return (
                    <SimpleGenin
                      key={npc.id}
                      npcId={npc.id}
                      position={[
                        npc.position[0],
                        npc.position[1] - 0.2,
                        npc.position[2],
                      ]}
                      facingDirection={npc.facingDirection}
                      health={npc.health}
                      behaviorState={npc.behaviorState}
                    />
                  );
                }
              })}

              {/* Render shurikens */}
              {gameState.shurikens.map((shuriken) => (
                <Shuriken
                  key={shuriken.id}
                  position={shuriken.position}
                  playerId={shuriken.playerId}
                />
              ))}

              {/* Render rasengans */}
              {gameState.rasengans?.map((rasengan) => (
                <Rasengan
                  key={rasengan.id}
                  position={rasengan.position}
                  playerId={rasengan.playerId}
                />
              ))}
            </Suspense>
            {isDebugMode ? (
              <OrbitDebugCamera />
            ) : (
              <FollowingCamera
                debugInfo={debugInfo}
                cameraConfig={cameraConfig}
              />
            )}
            {isDebugMode && <Stats />}
          </>
        ) : (
          <>
            {/* Mission world based on activeMission type */}
            {activeMission === 'contra' && (
              <ContraMissionScene
                onExit={exitMission}
                characterType={selectedCharacter || 'naruto'}
                incrementPlayerScore={incrementPlayerScore}
              />
            )}
            {/* Add more mission scenes here in the future */}
          </>
        )}
      </Canvas>

      {/* Mobile controls if on mobile device and not in character selection */}
      {isMobile && activeMission === 'none' && (
        <MobileControls onControlsChange={handleMobileControlsChange} />
      )}

      {/* Audio controls */}
      <AudioControls />

      {/* Debug UI if in debug mode */}
      {isDebugMode && (
        <CameraControlsUI
          cameraConfig={cameraConfig}
          setCameraConfig={setCameraConfig}
        />
      )}
      <DebugToggle
        isDebugMode={isDebugMode}
        setIsDebugMode={useDebugMode().setIsDebugMode}
      />

      <ScoreDisplay />

      {/* Score Animation Effect */}
      <ScoreAnimationEffect />
    </>
  );
}

function App() {
  return (
    <AudioProvider>
      <DebugProvider>
        <AppContent />
      </DebugProvider>
    </AudioProvider>
  );
}

function FollowingCamera({
  debugInfo,
  cameraConfig,
}: {
  debugInfo: DebugInfo;
  cameraConfig: CameraConfig;
}) {
  const { camera } = useThree();
  const player = useRef(new THREE.Vector3());
  const cameraTarget = useRef(new THREE.Vector3());
  const cameraPosition = useRef(new THREE.Vector3());

  useFrame(() => {
    const [x, y, z] = debugInfo.position;

    // Update player position reference
    player.current.set(x, y, z);

    // The target is simply the player position with a slight height offset
    // We look directly AT the player, not ahead of them
    cameraTarget.current.set(x, y + 1, z);

    // Fixed camera position: always above and behind player
    // We don't use player facing direction to rotate camera position

    // Start with a base position directly behind and above player
    const cameraOffsetX = 0;
    const cameraOffsetY = cameraConfig.height; // Height above player
    const cameraOffsetZ = cameraConfig.distance; // Distance behind player

    const idealPosition = new THREE.Vector3(
      x + cameraOffsetX,
      y + cameraOffsetY,
      z + cameraOffsetZ
    );

    // Apply very gentle smoothing
    const smoothFactor = 0.05;

    // Only update if meaningfully different
    if (camera.position.distanceToSquared(idealPosition) > 0.01) {
      cameraPosition.current.lerpVectors(
        camera.position,
        idealPosition,
        smoothFactor
      );
      camera.position.copy(cameraPosition.current);
    }

    // Make camera look down at the player
    camera.lookAt(cameraTarget.current);
  });

  return null;
}

export default App;
